import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTimes, FaCheck, FaArrowRight, FaArrowLeft, FaPlus, FaSpinner } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import './Getcover.css';

// ══════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════
const API = 'https://uat.definiteassurance.com/api';
const CERT_WEBHOOK = 'https://workflows.uuidesign.xyz/webhook/c924c7c4-d115-4724-a78c-27048b99b6f0';
const PAYMENT_WEBHOOK = 'https://workflows.uuidesign.xyz/webhook/0623c8f7-d286-45c4-b823-2675d25de35a';
const WALLET_WEBHOOK = 'https://sms.definiteassurance.com/webhook/04a58cac-a6b4-4bde-883a-b364f32d9ae9';
const DOUBLE_INS_WEBHOOK = 'https://sms.definiteassurance.com/webhook/d6c9608b-cd90-481a-995d-d40c908c96db';
const COMPANY = 'Definite';
const STAMP_DUTY = 40;
const DOUBLE_INS_DEBOUNCE_MS = 800;

// Product definitions
const PRODUCTS = [
  { code: 3617, name: 'Motor Private', img: '/private.jpg' },
  { code: 3582, name: 'Private Hire - Chauffeur Driven', img: '/chauffeur.jpg' },
  { code: 3583, name: 'Private Hire - Self Drive', img: '/self drive.jpg' },
  { code: 4694, name: 'Motor Private TOR', img: '/private.jpg' },
  { code: 4638, name: 'PSV MATATU (7-35)', img: '/psvmatatu.PNG' },
  { code: 3587, name: 'PSV BUS (36-105)', img: '/psvbus.jpg' },
  { code: 4652, name: 'PSV Boda Boda', img: '/boda.jpg' },
  { code: 4655, name: 'PSV Tuk Tuk', img: '/tuktukpsv.jpg' },
  { code: 4630, name: 'Motor Commercial', img: '/motorcom.jpg' },
  { code: 3592, name: 'Motor Commercial Agricultural', img: '/agricultural.jpg' },
  { code: 3604, name: 'Tuk Tuk Commercial', img: '/tuktuk.jpg' },
  { code: 4695, name: 'Motor Commercial TOR', img: '/motorcom.jpg' },
  { code: 4653, name: 'Motorcycle Private', img: '/motorcycleprivate.jpg' },
];

const TONNAGE_PRODUCTS = [3531, 3588, 4688, 4695, 3604, 4630, 3592];
const LOW_SUM_PRODUCTS = [4652, 4653, 4655, 3604]; 

// ══════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════
const formatter = (amount) =>
  Number(amount).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,');

const formatDateFromMs = (ms) => {
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const formatDateTime = (ms) => {
  const d = new Date(ms);
  const pad = (n, len = 2) => String(n).padStart(len, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
};

const getNow = () => formatDateTime(Date.now());

const getDaysInCurrentMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
};

const generateUniqueId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) result += chars[Math.floor(Math.random() * chars.length)];
  return result;
};

const validateKRAPin = (pin) => {
  const p = pin.toUpperCase();
  if (p.length !== 11) return { isValid: false, message: 'KRA PIN must be exactly 11 characters' };
  if (!/[A-Z]/.test(p[0]) || !/[A-Z]/.test(p[10])) return { isValid: false, message: 'Must start and end with a letter' };
  if (!/^[A-Z0-9]{9}$/.test(p.substring(1, 10))) return { isValid: false, message: 'Middle 9 characters must be alphanumeric' };
  return { isValid: true, message: 'Valid KRA PIN' };
};

const validateEmail = (email) => {
  const sanitized = email.replace(/[^a-zA-Z0-9@._-]/g, '');
  const valid = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(sanitized);
  return { isValid: valid, sanitized, message: valid ? 'Valid' : 'Invalid email format' };
};

// ══════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════
export default function Getcover() {
  const navigate = useNavigate();

  // ── Auth & agent ──
  const [credentials, setCredentials] = useState(null);
  const [isAuth, setIsAuth] = useState(false);
  const [agent, setAgent] = useState('');
  const [agentCode, setAgentCode] = useState('');
  const [agentName, setAgentName] = useState('');
  const [branch, setBranch] = useState(null);
  const [approved, setApproved] = useState(0);
  const [dealerCode, setDealerCode] = useState('');
  const [dsfApproved, setDsfApproved] = useState(0);
  const [parentIntermediary, setParentIntermediary] = useState(null);
  const [iraNumber, setIraNumber] = useState('');
  const [walletNo, setWalletNo] = useState('');
  const [walletBalance, setWalletBalance] = useState(0);
  const [availableCommission, setAvailableCommission] = useState(0);

  // ── Data ──
  const [clients, setClients] = useState([]);
  const [makemodel, setMakemodel] = useState(null);
  const [pcoveroptions, setPcoveroptions] = useState(null);
  const [models, setModels] = useState([]);

  // ── Step / UI ──
  const [step, setStep] = useState(1);
  const [productCode, setProductCode] = useState('');
  const [productName, setProductName] = useState('');
  const [prdCode, setPrdCode] = useState('');
  const [commissionRate, setCommissionRate] = useState('');
  const [iraCode, setIraCode] = useState('');
  const [riskClass, setRiskClass] = useState('');

  // ── Cover options modal (step 1 → get quote) ──
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [coverOptions, setCoverOptions] = useState([]);
  const [coverOptionCode, setCoverOptionCode] = useState('');
  const [coverOption, setCoverOption] = useState(null);
  const [insuredItems, setInsuredItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState('');
  const [capacity, setCapacity] = useState(0);
  const [sumInsured, setSumInsured] = useState(0);
  const [tonnage, setTonnage] = useState(0);
  const [coverPeriod, setCoverPeriod] = useState('');
  const [quoteText, setQuoteText] = useState('GET QUOTATION');

  // ── Quote results (step 2) ──
  const [premium, setPremium] = useState(null);
  const [PHCF, setPHCF] = useState(null);
  const [trainingLevy, setTrainingLevy] = useState(null);
  const [basicPremium, setBasicPremium] = useState(0);
  const [commission, setCommission] = useState('');
  const [netCommission, setNetCommission] = useState('');
  const [netPremium, setNetPremium] = useState('');
  const [payAmount, setPayAmount] = useState(0);

  // ── Risk details (step 3) ──
  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [client, setClient] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientPin, setClientPin] = useState('');
  const [clientFullname, setClientFullname] = useState('');

  const [cm, setCm] = useState('');
  const [commencementUnix, setCommencementUnix] = useState(null);
  const [commencementDate, setCommencementDate] = useState('');
  const [commencement, setCommencement] = useState('');
  const [ex, setEx] = useState('');
  const [expiryUnix, setExpiryUnix] = useState(null);
  const [expiryDate, setExpiryDate] = useState('');
  const [coverDays, setCoverDays] = useState('');
  const [numberOfDays] = useState(getDaysInCurrentMonth());
  const [today] = useState(getNow());

  const [reg, setReg] = useState('');
  const [searchTerm2, setSearchTerm2] = useState('');
  const [suggestions2, setSuggestions2] = useState([]);
  const [makeCode, setMakeCode] = useState('');
  const [makeName, setMakeName] = useState('');
  const [searchTerm3, setSearchTerm3] = useState('');
  const [suggestions3, setSuggestions3] = useState([]);
  const [modelCode, setModelCode] = useState('');
  const [modelName, setModelName] = useState('');
  const [bodyType, setBodyType] = useState('');
  const [chassis, setChassis] = useState('');
  const [engineNumber, setEngineNumber] = useState('');
  const [cc, setCC] = useState('');
  const [color, setColor] = useState('');
  const [yom, setYom] = useState('');
  const [file, setFile] = useState(null);
  const [confirmText, setConfirmText] = useState('CONFIRM DETAILS');

  // ── Double insurance ──
  const [doubleStatus, setDoubleStatus] = useState(null); // null=unchecked, true=found, false=clear
  const [doubleMessage, setDoubleMessage] = useState('');
  const [doubleChecking, setDoubleChecking] = useState(false);

  // ── New client modal ──
  const [showClientModal, setShowClientModal] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [otherNames, setOtherNames] = useState('');
  const [ncPhone, setNcPhone] = useState('');
  const [ncEmail, setNcEmail] = useState('');
  const [ncEmailValid, setNcEmailValid] = useState(true);
  const [ncEmailError, setNcEmailError] = useState('');
  const [idNumber, setIdNumber] = useState('');
  const [ncPin, setNcPin] = useState('');
  const [ncPinValid, setNcPinValid] = useState(null);
  const [ncPinError, setNcPinError] = useState('');
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [file3, setFile3] = useState(null);
  const [submitText, setSubmitText] = useState('SUBMIT');

  // ── Confirm modal ──
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isPressed2, setIsPressed2] = useState(false);
  const [confirmPayText, setConfirmPayText] = useState('CONFIRM DETAILS');

  // ── Payment modal ──
  const [showPayModal, setShowPayModal] = useState(false);
  const [policyNumber, setPolicyNumber] = useState('');
  const [proposalOID, setProposalOID] = useState('');
  const [insuredOID, setInsuredOID] = useState('');
  const [insuredSchedule, setInsuredSchedule] = useState('');
  const [coverType, setCoverType] = useState('');
  const [mpesaPhone, setMpesaPhone] = useState('');
  const [payText, setPayText] = useState('SEND REQUEST');
  const [resendText, setResendText] = useState('RESEND REQUEST');
  const [promptId, setPromptId] = useState(0);
  const [isMpesa, setIsMpesa] = useState(true);
  const [prompt, setPrompt] = useState(false);
  const [transactionId, setTransactionId] = useState('');
  const [retryText, setRetryText] = useState('RETRY');

  // ── Certificate ──
  const [isCert, setIsCert] = useState(0); // 0=pending, 1=success, 2=error
  const [certURL, setCertURL] = useState('');
  const [errorCode, setErrorCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // ── Mpesa verify modal ──
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [checkMpesa, setCheckMpesa] = useState(false);
  const [mpesaCode, setMpesaCode] = useState('');
  const [verifyText, setVerifyText] = useState('VERIFY');
  const [checkPayment, setCheckPayment] = useState('Checking payment please wait...');

  // ── Wallet / commission toggles ──
  const [paymentMode, setPaymentMode] = useState(0);
  const [utilize, setUtilize] = useState(0);

  // ══════════════════════════════════════
  //  AUTH HEADERS HELPER
  // ══════════════════════════════════════
  const authHeaders = useCallback(
    () => ({
      Authorization: `Basic ${credentials}`,
      'Content-Type': 'application/json',
    }),
    [credentials]
  );

  const apiFetch = useCallback(
    async (url, options = {}) => {
      const res = await fetch(url, {
        method: 'GET',
        headers: authHeaders(),
        ...options,
      });
      if (!res.ok) throw new Error(`API ${res.status}: ${res.statusText}`);
      return res.json();
    },
    [authHeaders]
  );

  // Abort controller ref for cleanup of in-flight requests
  const abortRef = useRef(null);

  // ══════════════════════════════════════
  //  LOGGING HELPER
  // ══════════════════════════════════════
  const insertLog = useCallback(
    async (agentId, activity) => {
      try {
        await fetch(`${API}/insertLogs?activity=${encodeURIComponent(activity)}&agent=${agentId}`, {
          headers: authHeaders(),
        });
      } catch (e) {
        console.error('Log insert failed', e);
      }
    },
    [authHeaders]
  );

  // ══════════════════════════════════════
  //  FILE UPLOAD HELPER
  // ══════════════════════════════════════
  const uploadFile = useCallback(async (fileData, agentId, proposalId, endpoint = 'uploadDocument') => {
    if (!fileData) return;
    const formData = new FormData();
    formData.append('file', fileData);
    formData.append('agent', agentId);
    formData.append('proposal', proposalId);
    try {
      await axios.post(`${API}/${endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    } catch (e) {
      console.error(`Upload ${endpoint} failed`, e);
    }
  }, []);

  const uploadClientDocs = useCallback(
    async (proposalId) => {
      await Promise.all([
        uploadFile(file1, agentCode, proposalId, 'uploadIdentity'),
        uploadFile(file2, agentCode, proposalId, 'uploadIRA'),
        uploadFile(file3, agentCode, proposalId, 'uploadKRA'),
      ]);
    },
    [file1, file2, file3, agentCode, uploadFile]
  );

  // ══════════════════════════════════════
  //  CERTIFICATE HELPERS
  // ══════════════════════════════════════
  const buildCertPayload = useCallback(() => ({
    IOID: insuredSchedule, insuredOID, dateDone: getNow(), selectedItem, proposalOID,
    coverType, clientName, policyNumber, commencementUnix, commencementDate,
    expiryUnix, expiryDate, reg, chassis,
    clientPhone: clientPhone ? clientPhone.slice(1) : '',
    bodyType, capacity, makeName, modelName, engineNumber, clientEmail,
    sumInsured, clientPin, yom, iraCode, tonnage, iraNumber,
  }), [insuredSchedule, insuredOID, selectedItem, proposalOID, coverType, clientName, policyNumber, commencementUnix, commencementDate, expiryUnix, expiryDate, reg, chassis, clientPhone, bodyType, capacity, makeName, modelName, engineNumber, clientEmail, sumInsured, clientPin, yom, iraCode, tonnage]);

  const insertCertificate = useCallback(async (certLink, certNo) => {
    try {
      await fetch(`${API}/insertCertificate`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ proposalOID, insuredOID, expiryUnix, commencementUnix, certNo, certLink, today }),
      });
    } catch (e) { console.error('insertCertificate failed', e); }
  }, [authHeaders, proposalOID, insuredOID, expiryUnix, commencementUnix, today]);

  const insertCertificateError = useCallback(async (errorMes) => {
    try {
      await fetch(`${API}/insertCertificateerror`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ proposalOID, insuredOID, expiryUnix, commencementUnix, errorMes, today }),
      });
    } catch (e) { console.error('insertCertificateError failed', e); }
  }, [authHeaders, proposalOID, insuredOID, expiryUnix, commencementUnix, today]);

  const generateCert = useCallback(async (statusSetter) => {
    statusSetter('GENERATING CERTIFICATE...');
    try {
      const res = await axios.post(CERT_WEBHOOK, buildCertPayload());
      if (res.data.success) {
        await insertCertificate(res.data.certURL, res.data.certNo);
        setCertURL(res.data.certURL);
        insertLog(agentCode, `Certificate for ${reg} generated`);
        setTimeout(() => setIsCert(1), 1500);
      } else {
        await insertCertificateError(res.data.errorMessage);
        insertLog(agentCode, `Certificate for ${reg} error-${res.data.errorMessage}`);
        setIsCert(2);
        setErrorCode(res.data.errorCode);
        setErrorMessage(res.data.errorMessage);
      }
    } catch (e) {
      toast.error('Certificate generation failed');
      console.error(e);
    }
  }, [buildCertPayload, insertCertificate, insertCertificateError, insertLog, agentCode, reg]);

  // ══════════════════════════════════════
  //  INITIAL DATA LOADS
  // ══════════════════════════════════════
  useEffect(() => {
    const user = localStorage.getItem('definiteagent');
    if (!user) { navigate('/login'); return; }
    setAgent(user);

    const controller = new AbortController();
    abortRef.current = controller;

    const init = async () => {
      try {
        // These 3 calls need no auth — fire in parallel
        const [credRes, mmRes, pcoRes] = await Promise.all([
          fetch(`${API}/getCredentials?username=${COMPANY}`, { signal: controller.signal }),
          fetch(`${API}/getmakesmodels`, { signal: controller.signal }),
          fetch(`${API}/getproductcoveroption2`, { signal: controller.signal }),
        ]);
        const credData = await credRes.json();
        const creds = btoa(`${credData.Username}:${credData.Password}`);
        setCredentials(creds);
        setIsAuth(true);

        // Start parsing makes/models and cover options in parallel
        const [mmData, pcoData] = await Promise.all([mmRes.json(), pcoRes.json()]);
        setMakemodel(mmData);
        setPcoveroptions(pcoData);
      } catch (e) {
        if (e.name !== 'AbortError') console.error('Init failed', e);
      }
    };
    init();

    return () => controller.abort();
  }, [navigate]);

  // ── Fetch clients ──
  const fetchClients = useCallback(async (agentId) => {
    try {
      const url = agentId ? `${API}/getClients?agent=${agentId}` : `${API}/getClients`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error('fetchClients failed');
      setClients(await res.json());
    } catch (e) { console.error('fetchClients failed', e); }
  }, [authHeaders]);

  // ── Single combined effect: agent details + wallet + commission + clients in parallel ──
  useEffect(() => {
    if (!agent || !isAuth || !credentials) return;
    const controller = new AbortController();
    const headers = { 'Content-Type': 'application/json', Authorization: `Basic ${credentials}` };
    const opts = { headers, signal: controller.signal };

    const loadAll = async () => {
      try {
        // Fire agent details + IRA number in parallel
        const [dealerRes, iraRes] = await Promise.all([
          fetch(`${API}/getDealerdetails?email=${agent}`, opts).then(r => r.json()),
          fetch(`${API}/getIranumber?agent=${agent}`, opts).then(r => r.json()),
        ]);

        const agentId = dealerRes.OID;
        setAgentCode(agentId);
        setAgentName(dealerRes.Dealer_name || agent);
        setBranch(dealerRes.Companyy);
        setDealerCode(dealerRes.Dealer_type);
        setDsfApproved(dealerRes.DSFAllowed);
        setParentIntermediary(dealerRes.ParentIntermediary);
        setApproved(dealerRes.Approved ? 1 : 0);
        setIraNumber(iraRes.IRANumber);

        // Now fire wallet + commission + clients in parallel (all need agentId)
        const [walRes, commRes] = await Promise.all([
          fetch(`${API}/getWallet?agent=${agentId}`, opts).then(r => r.json()),
          fetch(`${API}/getUtilizedCommission?agent=${agentId}`, opts).then(r => r.json()),
          fetchClients(agentId), // fires concurrently, writes its own state
        ]);

        const wNo = walRes.WalletNo;
        setWalletNo(wNo);
        setAvailableCommission(commRes.AvailableCommission > 0 ? commRes.AvailableCommission : 0);

        // Wallet balance — depends on WalletNo, but we already have it
        if (wNo) {
          const balRes = await fetch(`${API}/getWalletbalance?agent=${agentId}`, opts).then(r => r.json());
          setWalletBalance(balRes.Balance);
        }
      } catch (e) {
        if (e.name !== 'AbortError') console.error('Agent/wallet load failed', e);
      }
    };
    loadAll();

    return () => controller.abort();
  }, [agent, isAuth, credentials, fetchClients]);

  // ══════════════════════════════════════
  //  PRODUCT SELECTION & COVER OPTIONS
  // ══════════════════════════════════════
  useEffect(() => {
    if (!productCode || !isAuth) return;
    const load = async () => {
      try {
        const data = await apiFetch(`${API}/getThisproduct?OID=${productCode}`);
        setProductName(data.ProductDescription);
        setPrdCode(data.ProductCode.replace(/PRD-/g, ''));
        setCommissionRate(data.CommissionRate);
        setIraCode(data.IRACode);
        setRiskClass(data.RiskClass);
      } catch (e) { console.error(e); }
    };
    load();

    // Filter cover options from prefetched data
    if (pcoveroptions?.data) {
      const found = pcoveroptions.data.find((i) => i.product.OID == productCode);
      setCoverOptions(found ? found.coverOptions : []);
    }
  }, [productCode, isAuth, apiFetch, pcoveroptions]);

  // ── Cover option details ──
  useEffect(() => {
    if (!coverOptionCode || !isAuth) return;
    const load = async () => {
      try {
        const [itemRes, detRes] = await Promise.all([
          apiFetch(`${API}/getInsureditems?coverOption=${coverOptionCode}`),
          apiFetch(`${API}/getCoveroptiondetails?cover=${coverOptionCode}`),
        ]);
        setInsuredItems(itemRes);
        if (itemRes.length === 1) setSelectedItem(itemRes[0].OID);
        setCoverOption(detRes.CoverOption);
      } catch (e) { console.error(e); }
    };
    load();
  }, [coverOptionCode, isAuth, apiFetch]);

  // ── Cover type mapping ──
  useEffect(() => {
    if (coverOption === 1 || coverOption === 58) setCoverType(100);
    else if (coverOption === 2) setCoverType(200);
  }, [coverOption]);

  // ── Model name + body — combined into one effect ──
  useEffect(() => {
    if (!modelCode || !isAuth) return;
    let cancelled = false;
    const load = async () => {
      try {
        const modelData = await apiFetch(`${API}/getModelname?model=${modelCode}`);
        if (cancelled) return;
        setModelName(modelData.Description);
        if (modelData.BodyType) {
          const bodyData = await apiFetch(`${API}/getBodyname?body=${modelData.BodyType}`);
          if (!cancelled) setBodyType(bodyData.Description);
        }
      } catch (e) { if (!cancelled) console.error(e); }
    };
    load();
    return () => { cancelled = true; };
  }, [modelCode, isAuth, apiFetch]);

  // ── Client details ──
  // ── Client details — load once when client is selected, cache for confirm modal ──
  useEffect(() => {
    if (!selectedClient || !isAuth) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [idRes, detRes] = await Promise.all([
          apiFetch(`${API}/getClientid?client=${selectedClient}`),
          apiFetch(`${API}/getClientdetails?client=${selectedClient}`),
        ]);
        if (cancelled) return;
        setClient(idRes.IdentificationNo);
        setClientEmail(detRes.Email);
        setClientName(detRes.Dealer_name);
        setClientFullname(detRes.Dealer_name);
        setClientPin(detRes.Pin);
        setClientPhone(detRes.Mobile);
      } catch (e) { if (!cancelled) console.error(e); }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedClient, isAuth, apiFetch]);

  // ── Double insurance check — debounced to avoid hammering API on every keystroke ──
  const doubleInsTimerRef = useRef(null);
  useEffect(() => {
    // Clear any pending check
    if (doubleInsTimerRef.current) clearTimeout(doubleInsTimerRef.current);

    if (!reg || !chassis) {
      setDoubleStatus(null);
      setDoubleChecking(false);
      return;
    }

    setDoubleChecking(true);
    setDoubleStatus(null);

    doubleInsTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(DOUBLE_INS_WEBHOOK, {
          chasis: chassis, reg, commence: commencementDate, end: expiryDate,
        });
        if (res.data.success) {
          setDoubleStatus(true);
          setDoubleMessage(`Vehicle has active certificate in ${res.data.company}. Expected expiry: ${res.data.endDate}`);
        } else {
          setDoubleStatus(false);
          setDoubleMessage('No double insurance');
        }
      } catch (e) { console.error(e); }
      finally { setDoubleChecking(false); }
    }, DOUBLE_INS_DEBOUNCE_MS);

    return () => { if (doubleInsTimerRef.current) clearTimeout(doubleInsTimerRef.current); };
  }, [reg, chassis, commencementDate, expiryDate]);

  // ── Payment mode toggles ──
  useEffect(() => { setIsMpesa(paymentMode === 0 && utilize === 0); }, [paymentMode, utilize]);

  // ══════════════════════════════════════
  //  SEARCH HANDLERS — build lowercase lookup maps once
  // ══════════════════════════════════════

  // Pre-build lowercase searchable fields for clients (avoids repeated toLowerCase in filter)
  const clientsIndex = useMemo(
    () => clients.map(c => ({
      ...c,
      _search: `${c.FirstName} ${c.Surname} ${c.IdentificationNo}`.toLowerCase(),
    })),
    [clients]
  );

  const handleClientSearch = useCallback((e) => {
    const v = e.target.value;
    setSearchTerm(v);
    if (v.length > 0) {
      const q = v.toLowerCase();
      setSuggestions(clientsIndex.filter(c => c._search.includes(q)));
    } else setSuggestions([]);
  }, [clientsIndex]);

  // Pre-build lowercase make descriptions
  const makesIndex = useMemo(() => {
    if (!makemodel?.data) return [];
    return makemodel.data.map(i => ({
      make: i.make,
      models: i.models,
      _search: i.make.Description.toLowerCase(),
    }));
  }, [makemodel]);

  const handleMakeSearch = useCallback((e) => {
    const v = e.target.value;
    setSearchTerm2(v);
    if (v.length > 0) {
      const q = v.toLowerCase();
      setSuggestions2(makesIndex.filter(i => i._search.includes(q)).map(i => i.make));
    } else setSuggestions2([]);
  }, [makesIndex]);

  const handleModelSearch = useCallback((e) => {
    const v = e.target.value;
    setSearchTerm3(v);
    if (v.length > 0) {
      const q = v.toLowerCase();
      setSuggestions3(models.filter(m => m.Description.toLowerCase().includes(q)));
    } else setSuggestions3([]);
  }, [models]);

  const selectClient = (c) => { setSearchTerm(`${c.FirstName} ${c.Surname}`); setSelectedClient(c.OID); setSuggestions([]); };
  const selectMake = (m) => {
    setSearchTerm2(m.Description);
    setMakeCode(m.OID);
    setMakeName(m.Description);
    setSuggestions2([]);
    const found = makesIndex.find((i) => i.make.OID === m.OID);
    setModels(found ? found.models : []);
  };
  const selectModel = (m) => { setSearchTerm3(m.Description); setModelCode(m.OID); setSuggestions3([]); };

  // ══════════════════════════════════════
  //  DATE HANDLING
  // ══════════════════════════════════════
  const handleDateChange = (e) => {
    const ms = new Date(e.target.value).getTime() + 60000;
    if (ms < Date.now()) { toast.error('Commencement date cannot be in the past'); return; }

    setCommencement(ms);
    setCommencementDate(formatDateFromMs(ms));
    setCommencementUnix(formatDateTime(ms));
    setCm(formatDateTime(ms));

    // Calculate expiry
    const monthMs = { 30: 2592000000 - 86400000, 31: 2592000000, 28: 2419200000 - 86400000, 29: 2419200000 };
    const durations = {
      Monthly: monthMs[numberOfDays], '3installments': monthMs[numberOfDays], '6Installments': monthMs[numberOfDays],
      '10Installments': monthMs[numberOfDays], '2installments': monthMs[numberOfDays],
      Week: 604800000 - 86400000, Fortnight: 1209600000 - 86400000, Annually: 31536000000 - 86400000,
    };

    const exp = ms + (durations[coverPeriod] || 0);
    const expiryD = new Date(exp);
    expiryD.setHours(23, 59, 0, 0);
    const expMs = expiryD.getTime();

    setExpiryDate(formatDateFromMs(expMs));
    setExpiryUnix(formatDateTime(expMs));
    setEx(formatDateTime(expMs));

    const dayMap = { Monthly: 30, '3installments': 30, '6Installments': 30, '10Installments': 30, '2installments': 30, Week: 7, Fortnight: 14, Annually: 365 };
    setCoverDays(dayMap[coverPeriod] || 30);
  };

  // ══════════════════════════════════════
  //  PREMIUM CALCULATION HELPER
  // ══════════════════════════════════════
  const computePremiumResults = (premiumVal) => {
    const tl = Math.round(0.002 * premiumVal);
    const ph = Math.round(0.0025 * premiumVal);
    const gross = premiumVal + tl + ph + STAMP_DUTY;
    const com = Math.round((parseInt(commissionRate) / 100) * premiumVal);
    const netCom = com - Math.round(0.1 * com);

    setPremium(premiumVal);
    setTrainingLevy(tl);
    setPHCF(ph);
    setBasicPremium(gross);
    setPayAmount(gross);
    setCommission(com);
    setNetCommission(netCom);
    setNetPremium(gross - netCom);
  };

  // ══════════════════════════════════════
  //  GET QUOTATION
  // ══════════════════════════════════════
  const getQuote = async () => {
    if (dealerCode === 170 && dsfApproved !== '1') { toast.error('You need an agent account to proceed'); return; }
    if (!approved) { toast.error('Your account is not approved. Kindly contact support!'); insertLog(agentCode, 'Premium calculation failed! Account not approved'); return; }
    if (!productCode || !coverOptionCode) { toast.error('Insufficient information'); return; }

    const isComp = coverOption === 1 || coverOption === 58;
    const isTP = coverOption === 2;

    if (isComp) {
      const minSI = LOW_SUM_PRODUCTS.includes(Number(productCode)) ? 100000 : 500000;
      if (sumInsured < minSI) { toast.error(`Sum Insured cannot be below Kes. ${formatter(minSI)}`); return; }
    }
    if (isTP && !capacity) { toast.error('Specify capacity'); return; }
    if ([3582, 3583].includes(Number(productCode)) && Number(capacity) > 7) { toast.error('Capacity cannot exceed 7 for Private Hire products'); return; }
    if (isTP && Number(productCode) === 4695 && tonnage > 3) { toast.error('Tonnage should be below 3 Tons'); return; }

    try {
      setQuoteText('CALCULATING...');
      const params = new URLSearchParams({
        insuredItem: selectedItem, coverOption: coverOptionCode, sumInsured,
        capacity, coverPeriod, product: productCode, tonnage,
      });
      const data = await apiFetch(`${API}/calculatePremium?${params}`);

      if (data.premium) {
        if (data.insuredItem) setSelectedItem(data.insuredItem);
        computePremiumResults(data.premium);
        insertLog(agentCode, `Premium calculated for product ${productName}. Amount: Kes ${data.premium + Math.round(0.002 * data.premium) + Math.round(0.0025 * data.premium) + STAMP_DUTY}`);
        setShowQuoteModal(false);
        setStep(2);
      } else {
        toast.error('NO PREMIUM SET! CONTACT SUPPORT');
        insertLog(agentCode, `Premium for product ${productName} not found`);
      }
    } catch (e) {
      toast.error('Calculation failed');
      console.error(e);
    } finally {
      setQuoteText('GET QUOTATION');
    }
  };

  // ══════════════════════════════════════
  //  OPEN CONFIRM MODAL — uses already-cached client details, no re-fetch
  // ══════════════════════════════════════
  const openConfirmModal = () => {
    if (!selectedClient) { toast.error('Please select a client first'); return; }
    if (!clientFullname) { toast.error('Client details are still loading...'); return; }
    setShowConfirmModal(true);
  };

  // ══════════════════════════════════════
  //  CREATE PROPOSAL
  // ══════════════════════════════════════
  const proposalBody = useCallback(() => ({
    commissionRate, agentCode, agentName, prdCode, client, commencementUnix, expiryUnix,
    today, coverDays, reg, productCode, payAmount, premium, basicPremium, commission,
    stampduty: STAMP_DUTY, PHCF, trainingLevy, netCommission, netPremium, selectedClient,
    makeCode, modelCode, capacity, color, tonnage, yom, coverOptioncode: coverOptionCode,
    sumInsured, chassis, engineNumber, cc, selectedItem, branch,
  }), [commissionRate, agentCode, agentName, prdCode, client, commencementUnix, expiryUnix, today, coverDays, reg, productCode, payAmount, premium, basicPremium, commission, PHCF, trainingLevy, netCommission, netPremium, selectedClient, makeCode, modelCode, capacity, color, tonnage, yom, coverOptionCode, sumInsured, chassis, engineNumber, cc, selectedItem, branch]);

  const createNewProposal = async () => {
    setIsPressed2(true);
    setConfirmPayText('LOADING...');
    try {
      const checkRes = await apiFetch(`${API}/checkProposalexists?agent=${agentCode}&reg=${reg}`);

      let result;
      if (checkRes.exists1) {
        result = await (await fetch(`${API}/updateExisting`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ ...proposalBody(), selectedClient }),
          credentials: 'include',
        })).json();
      } else if (checkRes.exists2) {
        toast.error('A proposal with the same registration number already exists');
        return;
      } else {
        result = await (await fetch(`${API}/createProposal2`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify(proposalBody()),
          credentials: 'include',
        })).json();
      }

      if (result.success) {
        setInsuredSchedule(result.IOID);
        setPolicyNumber(result.policyNumber);
        setProposalOID(result.proposalOID);
        setInsuredOID(result.insuredOID);
        await uploadFile(file, agentCode, result.proposalOID);
        if (file1 && file3) await uploadClientDocs(result.proposalOID);
        insertLog(agentCode, `${checkRes.exists1 ? 'Updated' : 'New'} proposal. OID ${result.proposalOID}`);
        setShowConfirmModal(false);
        setShowPayModal(true);
      } else {
        toast.error('Proposal creation failed. Please try again.');
        setConfirmPayText('RETRY');
      }
    } catch (e) {
      toast.error('Network error! Please try again.');
      setConfirmPayText('RETRY');
      console.error(e);
    } finally {
      setIsPressed2(false);
    }
  };

  // ══════════════════════════════════════
  //  MAKE PAYMENT HANDLER
  // ══════════════════════════════════════
  const handleMakePayment = () => {
    if (confirmPayText === 'RETRY') {
      initiatePayment();
      return;
    }
    if (proposalOID) { setShowPayModal(true); return; }
    initiatePayment();
  };

  const initiatePayment = () => {
    if (!selectedClient || !commencement || !reg || !makeCode || !modelCode || !chassis) {
      toast.error('All fields are required!');
      return;
    }
    if (doubleStatus) { toast.error(doubleMessage); return; }
    if ((coverOption === 1 || coverOption === 58) && yom < 2011) {
      toast.error('Year of manufacture cannot be below 2010');
      return;
    }
    createNewProposal();
  };

  // ── Mpesa / Wallet / Commission payment ──
  const processPayment = async (statusSetter) => {
    const fmtPhone = '254' + mpesaPhone.slice(1);
    const ref = generateUniqueId();
    const uuid = uuidv4();
    const dateDone = getNow();

    if (paymentMode === 0 && utilize === 0) {
      // MPESA
      statusSetter('CHECKING PAYMENT...');
      toast.success('Prompt sent to phone!');
      setPrompt(true);
  
        const res = await axios.post(PAYMENT_WEBHOOK, { formattedNumber2: fmtPhone, payAmount:1, reg, uuid, qref: `QP${ref}`, dateDone }, { timeout: 30000 });
        if (res.data.success) {
          setTransactionId(res.data.transactionId);
          insertLog(agentCode, `Payment Kes.${payAmount} for ${reg} received - ${res.data.transactionId}`);
          // Approve & generate cert
          await fetch(`${API}/createProposal`, {
            method: 'POST', headers: authHeaders(),
            body: JSON.stringify({ utilize: 0, ...proposalBody(), proposalOID, transaction: res.data.transactionId }),
            credentials: 'include',
          });
          await uploadFile(file, agentCode, proposalOID);
          insertLog(agentCode, `Proposal ${proposalOID} approved`);
          //check if certificate has generated.
          
         const checkRes = await apiFetch(`${API}/checkCert?proposalOID=${proposalOID}`, { method: 'GET', headers: authHeaders() });
          const {CertificatePdf,success} = checkRes;
          if(success){
            setCertURL(CertificatePdf)
            insertLog(agentCode, `Certificate for ${proposalOID} generated`);
            setTimeout(() => setIsCert(1), 1500);
          } else {
           await generateCert(statusSetter);
          }

          
        } else {
          setPromptId(1);
          toast.error('PAYMENT NOT RECEIVED');
          statusSetter('SEND REQUEST');
        }

    } else if (paymentMode === 1) {
      // WALLET
      if (payAmount > walletBalance) { toast.error('INSUFFICIENT WALLET BALANCE. TOP UP TO PROCEED'); return; }
      statusSetter('CHECKING WALLET...');

      try {
      


        const paywithwallet = await fetch(`${API}/createProposalWithWallet`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ utilize: 0, ...proposalBody(), proposalOID }),
          credentials: 'include',
        });
        const payRes = await paywithwallet.json();
        if (!payRes.success) {
          toast.error('WALLET PAYMENT FAILED');
           insertLog(agentCode, `Payment for proposal ${proposalOID} done by wallet failed.`);
          return;
        }
        else {
           await uploadFile(file, agentCode, proposalOID);
          insertLog(agentCode, `Payment for proposal ${proposalOID} done by wallet and Approved`);
          await generateCert(statusSetter);

        }

       
      } catch (e) { toast.error('SOMETHING WENT WRONG'); statusSetter('SEND REQUEST'); }
    } else if (utilize === 1) {
      // COMMISSION UTILIZATION
      if (payAmount > availableCommission) { toast.error(`INSUFFICIENT COMMISSION`); return; }
      statusSetter('CHECKING BALANCE...');
      try {
        await fetch(`${API}/createProposal`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ utilize: 1, ...proposalBody(), proposalOID }),
          credentials: 'include',
        });
        await uploadFile(file, agentCode, proposalOID);
        insertLog(agentCode, `Payment by commission utilization for ${proposalOID}`);
        await generateCert(statusSetter);
      } catch (e) { toast.error('SOMETHING WENT WRONG'); statusSetter('SEND REQUEST'); }
    }
  };

  // ── Mpesa manual verify ──
  const checkAutoApprovalAndPay = async () => {
    try {
      const data = await apiFetch(`${API}/checkAutoApproval?proposal=${proposalOID}&payAmount=${payAmount}`);
      if (data.autoApproved && data.paid) {
        setShowVerifyModal(false);
        setIsMpesa(false);
        toast.success('Payment received');
        insertLog(agentCode, `Proposal ${proposalOID} approved`);
        await generateCert(setPayText);
      } else if (!data.autoApproved && data.paid) {
        setShowVerifyModal(false);
        toast.success('Payment received');
        // Manual approval + cert
        await fetch(`${API}/approveProposal`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ utilize: 0, ...proposalBody(), proposalOID, transaction: transactionId }),
          credentials: 'include',
        });
        await uploadFile(file, agentCode, proposalOID);
        await generateCert(setPayText);
      } else {
        setShowVerifyModal(true);
        setCheckMpesa(true);
      }
    } catch (e) { toast.error('Check failed'); console.error(e); }
  };

  const verifyMpesaCode = async () => {
    if (!mpesaCode) return;
    try {
      setVerifyText('Verifying...');
      const data = await apiFetch(`${API}/checkMpesa?code=${mpesaCode}&amount=${payAmount}&reg=${reg}`);
      if (data.success) {
        toast.success('Payment received');
        setShowVerifyModal(false);
        // Approve & generate
        await fetch(`${API}/approveProposal`, {
          method: 'POST', headers: authHeaders(),
          body: JSON.stringify({ utilize: 0, ...proposalBody(), proposalOID, transaction: mpesaCode }),
          credentials: 'include',
        });
        await uploadFile(file, agentCode, proposalOID);
        await generateCert(setPayText);
      } else {
        toast.error('No transaction with that code');
      }
    } catch (e) { toast.error('Verification failed'); }
    finally { setVerifyText('VERIFY'); }
  };

  const retryCertificate = async () => {
    setRetryText('Please wait...');
    await generateCert(setRetryText);
    setRetryText('RETRY');
  };

  // ══════════════════════════════════════
  //  CREATE CLIENT
  // ══════════════════════════════════════
  const createClient = async () => {
    if (!ncPinValid) { toast.error('KRA PIN is invalid'); return; }
    if (!ncPhone || ncPhone.length < 10) { toast.error('Phone must be at least 10 digits'); return; }
    if (!ncEmailValid) { toast.error('Email is invalid'); return; }
    if (`${firstName}${lastName}${otherNames}`.length > 50) { toast.error('Combined names must not exceed 50 chars'); return; }
    if (!firstName || !lastName || !ncPhone || !ncEmail || !idNumber || !ncPin || !file1 || !file3) {
      toast.error('All fields marked * are required');
      return;
    }

    try {
      setSubmitText('PLEASE WAIT...');
      const res = await fetch(`${API}/createClient`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ firstName, lastName, otherNames, phone: ncPhone, email: ncEmail, idNumber, pin: ncPin, agent: agentCode }),
        credentials: 'include',
      });
      const { success, exists, OID } = await res.json();

      if (success && !exists) {
        setSearchTerm(`${firstName} ${lastName}`);
        setSelectedClient(OID);
        fetchClients(agentCode);
        insertLog(agentCode, `Client ${firstName} ${lastName} created`);
        toast.success('Client added successfully');
        setShowClientModal(false);
        resetClientForm();
      } else if (!success && exists) {
        toast.error('Details already exist!');
      } else {
        toast.error('Something went wrong!');
      }
    } catch (e) { toast.error('Network error'); console.error(e); }
    finally { setSubmitText('SUBMIT'); }
  };

  const resetClientForm = () => {
    setFirstName(''); setLastName(''); setOtherNames('');
    setNcPhone(''); setNcEmail(''); setIdNumber(''); setNcPin('');
    setNcPinValid(null); setNcPinError(''); setNcEmailValid(true); setNcEmailError('');
  };

  const finish = () => window.location.reload(false);

  // ── Validation helpers ──
  const fieldClass = (valid) => (valid ? 'valid' : valid === false ? 'invalid' : '');

  // ══════════════════════════════════════
  //  PAYMENT INTERVAL OPTIONS — memoized
  // ══════════════════════════════════════
  const paymentIntervals = useMemo(() => {
    const pc = Number(productCode);
    const isMatatu = (pc === 4638 && coverOption === 2) || (pc === 3587 && coverOption === 2);
    const isTOR = pc === 4695 || pc === 4694;
    const isBoda = pc === 4652;

    if (isMatatu) {
      return [
        { value: 'Week', label: '7 Days' }, { value: 'Fortnight', label: '14 Days' },
        { value: 'Monthly', label: '30 Days' }, { value: 'Annually', label: 'Annual' },
        { value: '3Installments', label: '3 Installments' }, { value: '6Installments', label: '6 Installments' },
        { value: '10Installments', label: '10 Installments' },
      ];
    }
    if (isTOR) return [{ value: 'Monthly', label: '30 Days' }];
    if (isBoda && coverOptionCode === '3535') return [{ value: 'Monthly', label: '30 Days' }];
    if (isBoda && coverOptionCode === '3422') return [{ value: 'Annually', label: 'Annual' }, { value: '2installments', label: '2 Installments' }];
    if (coverOption === 1 || coverOption === 58) return [{ value: 'Monthly', label: '30 Days' }, { value: '3installments', label: '3 Installments' }];
    if (coverOption === 2) return [{ value: 'Annually', label: 'Annual' }, { value: '2installments', label: '2 Installments' }];
    return [];
  }, [productCode, coverOption, coverOptionCode]);

  // ══════════════════════════════════════
  //  RENDER
  // ══════════════════════════════════════
  return (
    <div className="getcover-page">
      <Header userName={agentName || agent} />
      <Sidebar />
      <ToastContainer position="top-right" autoClose={3000} />

      <main className="getcover-main">
        <div className="gc-top-row">
          <span className="gc-company">DEFINITE ASSURANCE</span>
          <span className="gc-breadcrumb">Get Cover / New Cover</span>
        </div>

        {/* ── Step indicator ── */}
        <div className="gc-steps">
          {['Select Product', 'Quotation', 'Risk Details'].map((label, i) => (
            <div key={i} className={`gc-step ${step === i + 1 ? 'active' : step > i + 1 ? 'completed' : ''}`}>
              <span className="gc-step-number">{step > i + 1 ? <FaCheck size={10} /> : i + 1}</span>
              {label}
            </div>
          ))}
        </div>

        {/* ══════════════════════════════════════
            STEP 1 — SELECT PRODUCT
           ══════════════════════════════════════ */}
        {step === 1 && (
          <div className="gc-card">
            <h3>Which cover do you want?</h3>
            {pcoveroptions === null && (
              <div className="gc-loading-inline" style={{ marginBottom: 16 }}>
                <FaSpinner className="gc-spinner-icon" />
                <span>Fetching cover options, please wait..</span>
              </div>
            )}
            <div className="gc-products-grid">
              {PRODUCTS.map((p) => (
                <div key={p.code} className="gc-product-card">
                  <img src={p.img} alt={p.name} className="gc-product-img" />
                  <button className="gc-product-name" onClick={() => { setProductCode(p.code); setShowQuoteModal(true); }}>
                    {p.name}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            STEP 2 — QUOTATION
           ══════════════════════════════════════ */}
        {step === 2 && (
          <div className="gc-card">
            <h3>Quotation Summary — {productName}</h3>
            <table className="gc-quote-table">
              <thead>
                <tr><th>Item</th><th style={{ textAlign: 'right' }}>Amount (KES)</th></tr>
              </thead>
              <tbody>
                <tr><td>Basic Premium</td><td>{formatter(premium)}</td></tr>
                <tr><td>Net Premium</td><td>{formatter(netPremium)}</td></tr>
                <tr><td>PHCF</td><td>{formatter(PHCF)}</td></tr>
                <tr><td>Training Levy</td><td>{formatter(trainingLevy)}</td></tr>
                <tr><td>Stamp Duty</td><td>{formatter(STAMP_DUTY)}</td></tr>
                <tr><td>Gross Premium</td><td>{formatter(basicPremium)}</td></tr>
                <tr><td>Gross Commission</td><td>{formatter(commission)}</td></tr>
              </tbody>
            </table>
            <div className="gc-btn-row">
              <button className="gc-btn gc-btn-danger" onClick={() => setStep(1)}><FaArrowLeft /> Previous</button>
              <button className="gc-btn gc-btn-success" onClick={() => setStep(3)}>Buy Cover <FaArrowRight /></button>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            STEP 3 — RISK DETAILS
           ══════════════════════════════════════ */}
        {step === 3 && (
          <div className="gc-card">
            <h3>Risk Details</h3>

            {/* Client search */}
            <div className="gc-form-section">
              <h4>Client Information</h4>
              <div className="gc-autocomplete">
                <div className="gc-form-group">
                  <label>Search Client by Name or ID *</label>
                  <input type="text" value={searchTerm} onChange={handleClientSearch} placeholder="Search by name or ID number" className={fieldClass(!!selectedClient)} />
                </div>
                {suggestions.length > 0 && (
                  <ul className="gc-suggestions">
                    {suggestions.map((c) => (
                      <li key={c.OID} onClick={() => selectClient(c)}>{c.FirstName} {c.Surname} — {c.IdentificationNo}</li>
                    ))}
                  </ul>
                )}
              </div>
              <button className="gc-new-client-btn" onClick={() => setShowClientModal(true)}><FaPlus /> New Client</button>
            </div>

            {/* Dates */}
            <div className="gc-form-section">
              <h4>Cover Period</h4>
              <div className="gc-form-row">
                <div className="gc-form-group">
                  <label>Commencement Date *</label>
                  <input type="datetime-local" onChange={handleDateChange} className={fieldClass(!!cm)} min={(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}T${String(n.getHours()).padStart(2, '0')}:${String(n.getMinutes()).padStart(2, '0')}`; })()} />
                </div>
                <div className="gc-form-group">
                  <label>Expiry Date</label>
                  <input type="text" value={ex || 'Expiry Date'} disabled className={fieldClass(!!ex)} />
                </div>
              </div>
            </div>

            {/* Vehicle */}
            <div className="gc-form-section">
              <h4>Vehicle Details</h4>
              <div className="gc-form-group" style={{ marginBottom: 14 }}>
                <label>Registration Number *</label>
                <input type="text" value={reg} onChange={(e) => setReg(e.target.value.replace(/\s+/g, '').toUpperCase())} placeholder="e.g. KAA 000A" className={fieldClass(!!reg)} />
              </div>

              {/* Double insurance status */}
              {doubleChecking && <div className="gc-double-status loading">Checking double insurance...</div>}
              {doubleStatus === true && <div className="gc-double-status danger">{doubleMessage}</div>}
              {doubleStatus === false && <div className="gc-double-status success">{doubleMessage}</div>}

              <div className="gc-form-row">
                <div className="gc-autocomplete gc-form-group">
                  <label>Vehicle Make *</label>
                  <input type="text" value={searchTerm2} onChange={handleMakeSearch} placeholder="Search make" className={fieldClass(!!makeCode)} />
                  {suggestions2.length > 0 && (
                    <ul className="gc-suggestions">{suggestions2.map((m) => (<li key={m.OID} onClick={() => selectMake(m)}>{m.Description}</li>))}</ul>
                  )}
                </div>
                <div className="gc-autocomplete gc-form-group">
                  <label>Vehicle Model *</label>
                  <input type="text" value={searchTerm3} onChange={handleModelSearch} placeholder="Search model" className={fieldClass(!!modelCode)} />
                  {suggestions3.length > 0 && (
                    <ul className="gc-suggestions">{suggestions3.map((m) => (<li key={m.OID} onClick={() => selectModel(m)}>{m.Description}</li>))}</ul>
                  )}
                </div>
              </div>

              <div className="gc-form-row">
                <div className="gc-form-group">
                  <label>Chassis Number *</label>
                  <input type="text" value={chassis} onChange={(e) => setChassis(e.target.value)} className={fieldClass(!!chassis)} />
                </div>
                <div className="gc-form-group">
                  <label>Engine Number</label>
                  <input type="text" value={engineNumber} onChange={(e) => setEngineNumber(e.target.value)} className={fieldClass(!!engineNumber)} />
                </div>
              </div>
              <div className="gc-form-row">
                <div className="gc-form-group">
                  <label>Engine Rating (CC)</label>
                  <input type="text" value={cc} onChange={(e) => setCC(e.target.value.replace(/\D/g, ''))} placeholder="e.g. 1500" className={fieldClass(!!cc)} />
                </div>
                <div className="gc-form-group">
                  <label>Color</label>
                  <input type="text" value={color} onChange={(e) => setColor(e.target.value)} className={fieldClass(!!color)} />
                </div>
              </div>
              <div className="gc-form-row">
                <div className="gc-form-group">
                  <label>Year of Manufacture *</label>
                  <input type="number" value={yom} onChange={(e) => setYom(e.target.value)} className={fieldClass(!!yom)} />
                </div>
                <div className="gc-form-group">
                  <label>Log Book / Previous Certificate</label>
                  <input type="file" onChange={(e) => setFile(e.target.files[0])} />
                </div>
              </div>
            </div>

            <div className="gc-btn-row">
              <button className="gc-btn gc-btn-danger" onClick={() => setStep(2)}><FaArrowLeft /> Previous</button>
              <button className="gc-btn gc-btn-success" onClick={openConfirmModal}>{confirmText}</button>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            QUOTE MODAL (Cover options)
           ══════════════════════════════════════ */}
        {showQuoteModal && (
          <div className="gc-modal-overlay" onClick={() => setShowQuoteModal(false)}>
            <div className="gc-modal" onClick={(e) => e.stopPropagation()}>
              <div className="gc-modal-header">
                <h3>New Cover — {PRODUCTS.find((p) => p.code === Number(productCode))?.name}</h3>
                <button className="gc-modal-close" onClick={() => setShowQuoteModal(false)}><FaTimes /></button>
              </div>
              <div className="gc-modal-body">
                <div className="gc-form-group" style={{ marginBottom: 14 }}>
                  <label>Cover Option</label>
                  {pcoveroptions === null ? (
                    <div className="gc-loading-inline">
                      <FaSpinner className="gc-spinner-icon" />
                      <span>Fetching cover options…</span>
                    </div>
                  ) : (
                    <select value={coverOptionCode} onChange={(e) => setCoverOptionCode(e.target.value)}>
                      <option value="" disabled>Select cover option</option>
                      {coverOptions.map((o) => (<option key={o.OID} value={o.OID}>{o.OptionName}</option>))}
                    </select>
                  )}
                </div>
                {(coverOption === 1 || coverOption === 58) && (
                  <div className="gc-form-group" style={{ marginBottom: 14 }}>
                    <label>Sum Insured</label>
                    <input type="number" value={sumInsured} onChange={(e) => setSumInsured(e.target.value)} placeholder="Sum insured" />
                  </div>
                )}
                <div className="gc-form-group" style={{ marginBottom: 14 }}>
                  <label>Seating Capacity{[3582, 3583].includes(Number(productCode)) ? ' (max 7)' : ''}</label>
                  <input
                    type="number"
                    value={capacity}
                    placeholder="Seating capacity"
                    max={[3582, 3583].includes(Number(productCode)) ? 7 : undefined}
                    onChange={(e) => {
                      const val = e.target.value;
                      if ([3582, 3583].includes(Number(productCode)) && Number(val) > 7) {
                        toast.error('Capacity cannot exceed 7 for this product');
                        setCapacity(7);
                      } else {
                        setCapacity(val);
                      }
                    }}
                  />
                  {[3582, 3583].includes(Number(productCode)) && Number(capacity) > 7 && (
                    <span className="gc-field-error">Capacity cannot exceed 7 for Private Hire</span>
                  )}
                </div>
                {TONNAGE_PRODUCTS.includes(Number(productCode)) && (
                  <div className="gc-form-group" style={{ marginBottom: 14 }}>
                    <label>Tonnage (Load Capacity)</label>
                    <input type="text" value={tonnage} onChange={(e) => setTonnage(e.target.value)} />
                  </div>
                )}
                <div className="gc-form-group" style={{ marginBottom: 14 }}>
                  <label>Payment Interval</label>
                  {coverOptionCode && coverOption === null ? (
                    <div className="gc-loading-inline">
                      <FaSpinner className="gc-spinner-icon" />
                      <span>Fetching payment intervals…</span>
                    </div>
                  ) : (
                    <select value={coverPeriod} onChange={(e) => setCoverPeriod(e.target.value)}>
                      <option value="" disabled>Number of Installments</option>
                      {paymentIntervals.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                    </select>
                  )}
                </div>
                <button className="gc-btn gc-btn-success" style={{ width: '100%' }} onClick={getQuote} disabled={quoteText !== 'GET QUOTATION'}>
                  {quoteText}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            CONFIRM DETAILS MODAL
           ══════════════════════════════════════ */}
        {showConfirmModal && (
          <div className="gc-modal-overlay" onClick={() => setShowConfirmModal(false)}>
            <div className="gc-modal gc-modal-lg" onClick={(e) => e.stopPropagation()}>
              <div className="gc-modal-header">
                <h3>Confirm Details</h3>
                <button className="gc-modal-close" onClick={() => setShowConfirmModal(false)}><FaTimes /></button>
              </div>
              <div className="gc-modal-body">
                <div className="gc-confirm-section">
                  <h5>Insured Details</h5>
                  <div className="gc-confirm-grid">
                    <div className="gc-confirm-item"><strong>Name:</strong> {clientFullname}</div>
                    <div className="gc-confirm-item"><strong>Phone:</strong> {clientPhone}</div>
                    <div className="gc-confirm-item"><strong>Email:</strong> {clientEmail}</div>
                    <div className="gc-confirm-item"><strong>KRA PIN:</strong> {clientPin}</div>
                  </div>
                </div>
                <div className="gc-confirm-section">
                  <h5>Vehicle Details</h5>
                  <div className="gc-confirm-grid">
                    <div className="gc-confirm-item"><strong>Reg:</strong> <span style={{ color: '#e74c3c', fontWeight: 700 }}>{reg}</span></div>
                    <div className="gc-confirm-item"><strong>Make:</strong> {makeName}</div>
                    <div className="gc-confirm-item"><strong>Model:</strong> {modelName}</div>
                    <div className="gc-confirm-item"><strong>Year:</strong> {yom}</div>
                  </div>
                </div>
                <div className="gc-confirm-section">
                  <h5>Premium Details</h5>
                  <div className="gc-confirm-grid">
                    <div className="gc-confirm-item"><strong>Product:</strong> {productName}</div>
                    <div className="gc-confirm-item"><strong>Interval:</strong> {coverPeriod}</div>
                    <div className="gc-confirm-item"><strong>Sum Insured:</strong> Kes. {formatter(sumInsured)}</div>
                    <div className="gc-confirm-item"><strong>Premium:</strong> <span style={{ color: '#27ae60', fontWeight: 700, fontSize: '1.1rem' }}>Kes. {formatter(basicPremium)}</span></div>
                  </div>
                </div>
                <div className="gc-btn-row" style={{ justifyContent: 'flex-end' }}>
                  <button className="gc-btn gc-btn-outline" onClick={() => setShowConfirmModal(false)}>Close</button>
                  <button className="gc-btn gc-btn-success" onClick={handleMakePayment} disabled={isPressed2}>
                    {isPressed2 ? 'Please wait...' : confirmPayText}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            PAYMENT MODAL
           ══════════════════════════════════════ */}
        {showPayModal && (
          <div className="gc-modal-overlay">
            <div className="gc-modal" onClick={(e) => e.stopPropagation()}>
              <div className="gc-modal-header">
                <h3>Make Payment</h3>
                {isCert === 0 && <button className="gc-modal-close" onClick={() => { setShowPayModal(false); setIsCert(0); setPrompt(false); setPayText('SEND REQUEST'); }}><FaTimes /></button>}
              </div>
              <div className="gc-modal-body">
                  <div className="gc-form-group" style={{ marginBottom: 16 }}>
                      <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Select Payment Method:</label>
                      <div style={{ display: 'flex', gap: 24 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="radio"
                            name="paymentMethod"
                            checked={isMpesa && paymentMode === 0}
                            onChange={() => {
                              setIsMpesa(true);
                              setPaymentMode(0);
                            }}
                          />
                          Lipa na Mpesa
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <input
                            type="radio"
                            name="paymentMethod"
                            checked={!isMpesa && paymentMode === 1}
                            onChange={() => {
                              setIsMpesa(false);
                              setPaymentMode(1);
                            }}
                          />
                          Pay from Wallet
                        </label>
                      </div>
                    </div>
                {isCert === 0 && (
                  <>
                    {/* Payment Method Selection */}
                  
                    {/* End Payment Method Selection */}
                    <div className="gc-pay-amount">
                      <p>Amount to pay</p>
                      <h4>KES. {formatter(payAmount)}</h4>
                    </div>
                    {isMpesa && (
                      <div className="gc-form-group" style={{ marginBottom: 14 }}>
                        <label>Mpesa Number *</label>
                        <input type="text" value={mpesaPhone} onChange={(e) => setMpesaPhone(e.target.value)} placeholder="e.g. 0712345678" />
                      </div>
                    )}
                    {paymentMode === 1 && <p style={{ fontSize: '0.85rem', color: '#555' }}>Available Wallet Balance: <strong>KES. {formatter(walletBalance)}</strong></p>}
                    {utilize === 1 && <p style={{ fontSize: '0.85rem', color: '#555' }}>Available Commission: <strong>KES. {availableCommission}</strong></p>}

                    {paymentMode === 0 &&
                      <>
                      
                         {promptId === 0 && (
                      <button className="gc-btn gc-btn-success" style={{ width: '100%', marginBottom: 12 }} onClick={() => processPayment(setPayText)} disabled={payText !== 'SEND REQUEST'}>
                        {payText}
                          </button>
                        )}
                        {promptId === 1 && (
                          <button className="gc-btn gc-btn-success" style={{ width: '100%', marginBottom: 12 }} onClick={() => processPayment(setResendText)} disabled={resendText !== 'RESEND REQUEST'}>
                            {resendText}
                          </button>
                        )}
                      
                      </>
                    
                    }

                    {paymentMode === 1 && 

                      <>
                        {walletBalance < payAmount && <p style={{ color: '#c0392b', marginBottom: 12,fontSize:14 }}>Insufficient wallet balance. Please top up to proceed.</p>}
                        {walletBalance >= payAmount && 
                          <button className="gc-btn gc-btn-success" style={{ width: '100%', marginBottom: 12 }} onClick={() => processPayment(setPayText)} disabled={payText !== 'SEND REQUEST'}>
                            {payText}
                          </button>
                      }
                      
                      </>
                    }



                   

                    {isMpesa && !prompt && (
                      <>
                        <div className="gc-separator">─── OR ───</div>
                        <div className="gc-mpesa-steps">
                          <p>1. Go to MPESA Toolkit</p>
                          <p>2. Select Lipa na Mpesa</p>
                          <p>3. Select Paybill</p>
                          <p>4. Enter Paybill Number <strong>200300</strong></p>
                          <p>5. Enter Account <strong>{reg}</strong></p>
                          <p>6. Amount to Pay <strong>KES. {payAmount}</strong></p>
                          <p>7. Enter MPESA PIN and Complete</p>
                          <button className="gc-already-paid" onClick={checkAutoApprovalAndPay}>Already paid? Click here</button>
                        </div>
                      </>
                    )}
                  </>
                )}

                {isCert === 1 && (
                  <div className="gc-cert-success">
                    <a href={certURL} target="_blank" rel="noreferrer">DOWNLOAD CERTIFICATE</a>
                    <p style={{ fontSize: '0.82rem', color: '#888', marginTop: 10 }}>If certificate does not download, click the button again.</p>
                    <button className="gc-btn gc-btn-success" style={{ marginTop: 16 }} onClick={finish}>FINISH</button>
                  </div>
                )}

                {isCert === 2 && (
                  <div className="gc-cert-error">
                    <p>ERROR: {errorMessage}</p>
                    <button className="gc-btn gc-btn-success" onClick={retryCertificate}>{retryText}</button>
                    <br /><br />
                    <button className="gc-btn gc-btn-outline" onClick={finish}>FINISH</button>
                  </div>
                )}

                {isCert === 0 && (
                  <div className="gc-dmvic-errors">
                    <h6>Common DMVIC Errors</h6>
                    <p className="gc-error-title">Please note that the details entered do not match NTSA records</p>
                    <p className="gc-error-desc">The portal will generate certificate and send to your email despite this error. If not, navigate to 'Failed Certificates', search and click 'Retry'.</p>
                    <p className="gc-error-title">Invalid insured pin</p>
                    <p className="gc-error-desc">Navigate to 'Failed Certificates', click 'Edit', change KRA Pin, 'Save changes' and 'Retry'.</p>
                    <p className="gc-error-title">Invalid insured email</p>
                    <p className="gc-error-desc">Navigate to 'Failed Certificates', click 'Edit', change email, 'Save changes' and 'Retry'.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            VERIFY MPESA MODAL
           ══════════════════════════════════════ */}
        {showVerifyModal && (
          <div className="gc-modal-overlay">
            <div className="gc-modal" onClick={(e) => e.stopPropagation()}>
              <div className="gc-modal-header">
                <h3>Confirm Payment</h3>
                <button className="gc-modal-close" onClick={() => { setShowVerifyModal(false); setCheckMpesa(false); }}><FaTimes /></button>
              </div>
              <div className="gc-modal-body">
                {!checkMpesa && <p style={{ textAlign: 'center' }}>{checkPayment}</p>}
                {checkMpesa && (
                  <>
                    <p style={{ color: '#c0392b', marginBottom: 12 }}>Enter the M-Pesa transaction code below</p>
                    <div className="gc-form-group" style={{ marginBottom: 14 }}>
                      <label>Transaction Code</label>
                      <input type="text" value={mpesaCode} onChange={(e) => setMpesaCode(e.target.value)} placeholder="e.g. SBK1234ABC" />
                    </div>
                    <button className="gc-btn gc-btn-success" style={{ width: '100%' }} onClick={verifyMpesaCode} disabled={verifyText !== 'VERIFY'}>
                      {verifyText}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            NEW CLIENT MODAL
           ══════════════════════════════════════ */}
        {showClientModal && (
          <div className="gc-modal-overlay" onClick={() => setShowClientModal(false)}>
            <div className="gc-modal gc-modal-lg" onClick={(e) => e.stopPropagation()}>
              <div className="gc-modal-header">
                <h3>New Client</h3>
                <button className="gc-modal-close" onClick={() => setShowClientModal(false)}><FaTimes /></button>
              </div>
              <div className="gc-modal-body">
                <div className="gc-form-row">
                  <div className="gc-form-group">
                    <label>First Name *</label>
                    <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  </div>
                  <div className="gc-form-group">
                    <label>Last Name *</label>
                    <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  </div>
                </div>
                <div className="gc-form-row">
                  <div className="gc-form-group">
                    <label>Other Names</label>
                    <input type="text" value={otherNames} onChange={(e) => setOtherNames(e.target.value)} />
                  </div>
                  <div className="gc-form-group">
                    <label>Phone Number *</label>
                    <input type="text" value={ncPhone} onChange={(e) => setNcPhone(e.target.value)} className={fieldClass(ncPhone.length >= 10 ? true : ncPhone.length > 0 ? false : null)} />
                    {ncPhone.length > 0 && ncPhone.length < 10 && <span className="gc-field-error">Min 10 digits</span>}
                  </div>
                </div>
                {`${firstName}${lastName}${otherNames}`.length > 50 && (
                  <span className="gc-field-error">Combined name length ({`${firstName}${lastName}${otherNames}`.length}/50) exceeds limit</span>
                )}
                <div className="gc-form-row">
                  <div className="gc-form-group">
                    <label>Email Address *</label>
                    <input type="text" value={ncEmail} onChange={(e) => {
                      const v = e.target.value;
                      if (!v) { setNcEmail(''); setNcEmailError(''); setNcEmailValid(true); return; }
                      const res = validateEmail(v);
                      setNcEmail(res.isValid ? res.sanitized : v);
                      setNcEmailError(res.isValid ? '' : res.message);
                      setNcEmailValid(res.isValid);
                    }} className={fieldClass(ncEmail ? ncEmailValid : null)} />
                    {ncEmailError && <span className="gc-field-error">{ncEmailError}</span>}
                  </div>
                  <div className="gc-form-group">
                    <label>ID Number *</label>
                    <input type="text" value={idNumber} onChange={(e) => setIdNumber(e.target.value)} />
                  </div>
                </div>
                <div className="gc-form-group" style={{ marginBottom: 16 }}>
                  <label>KRA PIN *</label>
                  <input type="text" value={ncPin} maxLength={11} style={{ textTransform: 'uppercase' }}
                    onChange={(e) => {
                      const v = e.target.value.toUpperCase();
                      setNcPin(v);
                      if (v.length === 11) { const r = validateKRAPin(v); setNcPinError(r.isValid ? '' : r.message); setNcPinValid(r.isValid); }
                      else if (v.length > 0) { setNcPinError('Must be 11 characters'); setNcPinValid(false); }
                      else { setNcPinError(''); setNcPinValid(null); }
                    }}
                    className={fieldClass(ncPinValid)} />
                  {ncPinError && <span className="gc-field-error">{ncPinError}</span>}
                </div>

                <h4 style={{ fontSize: '0.88rem', fontWeight: 700, marginBottom: 10 }}>Required Documents</h4>
                <div className="gc-upload-row">
                  <div className="gc-upload-item">
                    <label>Identification *</label>
                    <input type="file" onChange={(e) => setFile1(e.target.files[0])} />
                  </div>
                  <div className="gc-upload-item">
                    <label>KRA PIN *</label>
                    <input type="file" onChange={(e) => setFile3(e.target.files[0])} />
                  </div>
                  <div className="gc-upload-item">
                    <label>Any Other</label>
                    <input type="file" onChange={(e) => setFile2(e.target.files[0])} />
                  </div>
                </div>

                <button className="gc-btn gc-btn-success" style={{ width: '100%' }} onClick={createClient}
                  disabled={submitText !== 'SUBMIT' || !ncPinValid || (ncPhone.length > 0 && ncPhone.length < 10) || `${firstName}${lastName}${otherNames}`.length > 50 || !ncEmailValid}>
                  {submitText}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
